/**
 * ConsentPrompt — aviso in-app de UNA sola vez (Paquete 3) pidiendo los dos consentimientos de marketing.
 * GET /users/me/consents → si `prompted` es falso se muestra; «Guardar» (PUT) o «Ahora no» (POST prompt-seen) lo cierran para siempre.
 * No se muestra sin sesión, en /auth ni en /legal/*; cede el paso al LegalAcceptanceGate (z-index menor).
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { tr } from '@/contexts/LanguageContext';
import { ConsentCheckboxes, RelationToggles } from '@/components/Consent/ConsentCheckboxes';

export const ConsentPrompt = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [state, setState] = useState(null);
  const [value, setValue] = useState({ platform_news: false });
  const [relations, setRelations] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) { setState(null); return undefined; }
    let alive = true;
    api.get('/users/me/consents').then((d) => { if (alive) { setState(d); setRelations(d.relations || []); } }).catch(() => {});
    return () => { alive = false; };
  }, [user?.id]);

  if (!user || !state || state.prompted || pathname.startsWith('/legal/') || pathname.startsWith('/auth')) return null;

  const close = () => setState({ ...state, prompted: true });
  const save = async () => {
    setSaving(true);
    try {
      await api.put('/users/me/consents', { platform_news: value.platform_news, relations: relations.map((r) => ({ id: r.id, barber_promos: r.barber_promos })) });
      toast.success(tr("Guardamos tus preferencias de correo."));
      close();
    } catch {
      toast.error(tr("No pudimos guardar tus preferencias. Inténtalo de nuevo."));
    } finally {
      setSaving(false);
    }
  };
  const later = async () => {
    close();
    try { await api.post('/users/me/consents/prompt-seen', {}); } catch { /* best-effort */ }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-background/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="consent-prompt">
      <div className="w-full max-w-md rounded-3xl border border-primary/30 bg-card p-6 space-y-4 shadow-2xl">
        <p className="text-[10px] uppercase tracking-[0.25em] text-primary font-bold flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Ziyup</p>
        <h2 className="font-display text-xl font-bold text-foreground">{tr("¿Qué correos quieres recibir?")}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">{tr("Elige por separado. Puedes cambiarlo cuando quieras en Ajustes → Privacidad, y cada correo trae su enlace de baja.")}</p>
        <RelationToggles relations={relations} onToggle={(r, v) => setRelations(relations.map((x) => (x.id === r.id ? { ...x, barber_promos: v } : x)))} testPrefix="consent-prompt-rel" />
        <ConsentCheckboxes value={value} onChange={setValue} fields={['platform_news']} testPrefix="consent-prompt" />
        <div className="flex gap-2">
          <button type="button" onClick={later} disabled={saving}
            className="flex-1 h-11 rounded-full border border-border text-sm text-muted-foreground hover:bg-secondary/40 transition-colors disabled:opacity-60"
            data-testid="consent-prompt-later">{tr("Ahora no")}</button>
          <button type="button" onClick={save} disabled={saving}
            className="flex-1 h-11 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
            data-testid="consent-prompt-save">{saving ? tr("Guardando…") : tr("Guardar")}</button>
        </div>
      </div>
    </div>
  );
};

export default ConsentPrompt;
